import Box from '@components/Box';
import CompletionModalWindow from '@components/modals/CompletionModalWindow';
import ConfirmPhone from '@components/ConfirmPhone';
import TextInputContainer from '@containers/TextInputContainer';
import WrapperDetails from './WrapperDetails';
import WrapperDetailsMessage from './WrapperDetailsMessage/WrapperDetailsMessage';
import { AxiosError } from 'axios';
import { Grid, Row } from '@components/Grid';
import { Modal } from '@components/Modal';
import { TProfilePhoneForm } from './../types';
import { updateProfilePhone } from '@api/apiClient';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useProfile } from '@contexts/ProfileContext';
import { useTranslation } from 'react-i18next';

const PhoneDetails: React.FC = (): React.ReactElement => {
  const [isOpenCompletionWindow, setIsOpenCompletionWindow] = useState<boolean>(false);
  const [isOpenConfirmPhoneWindow, setIsOpenConfirmPhoneWindow] = useState<boolean>(false);

  const { account, get } = useProfile();
  const { t } = useTranslation();

  const {
    error: errorUpdateProfilePhone,
    isError: isErrorUpdateProfilePhone,
    isLoading: isLoadingUpdateProfilePhone,
    mutate: mutateUpdateProfilePhone,
    reset: resetUpdateProfilePhone
  } = useMutation<unknown, AxiosError<any>, TProfilePhoneForm>({
    mutationFn: updateProfilePhone,
    onSuccess: (_, variables) => {
      reset(variables);
      setIsOpenConfirmPhoneWindow(true);
    }
  });

  const { control, formState, handleSubmit, reset, setValue, watch } = useForm<TProfilePhoneForm>({
    defaultValues: {
      name: '',
      phone: account?.phone ?? ''
    }
  });

  useEffect(() => {
    if (account?.phone) {
      setValue('phone', account.phone, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false
      });
    }
  }, [account?.phone, setValue]);

  const phone = watch('phone');

  const submit = (form: TProfilePhoneForm) => {
    const data: TProfilePhoneForm = {
      name: null,
      phone: form.phone
    };
    mutateUpdateProfilePhone(data);
  };

  return (
      <>
        <WrapperDetails
            btnAction={() => {
              handleSubmit(submit)();
              resetUpdateProfilePhone();
            }}
            btnDisabled={!formState.isDirty}
            btnPending={isLoadingUpdateProfilePhone}
            btnText={t('profileData.buttonSave')}
            error={errorUpdateProfilePhone}
            errorPrefix="profileData.phoneDetails.errors"
            isActionVisible
            isError={isErrorUpdateProfilePhone}
            title={t('profileData.phoneDetails.title')}
        >
          <Grid>
            <Row>
              <Box pb={3}>
                <WrapperDetailsMessage
                    description={t('profileData.phoneDetails.message.description')}
                    title={t('profileData.phoneDetails.message.title')}
                />
              </Box>
            </Row>
            <Row>
              <Box>
                <TextInputContainer
                    control={control}
                    hasError={errorUpdateProfilePhone?.response?.data?.code === 'pin-code-rate-limit'}
                    name="phone"
                    rules={{ required: t('defaultFormErrors.required') }}
                    title={t('profileData.phone')}
                    type="phone"
                />
              </Box>
            </Row>
          </Grid>
        </WrapperDetails>
        <Modal
            isOpen={isOpenConfirmPhoneWindow}
            onClickOverlay={() => setIsOpenConfirmPhoneWindow(false)}
            onClose={() => setIsOpenConfirmPhoneWindow(false)}
        >
          <ConfirmPhone
              isLoading={isLoadingUpdateProfilePhone}
              onSuccess={() => {
                get();
                setIsOpenCompletionWindow(true);
                setIsOpenConfirmPhoneWindow(false);
              }}
              phone={phone!}
              resend={handleSubmit(submit)}
          />
        </Modal>
        <Modal
            isOpen={isOpenCompletionWindow}
            onClickOverlay={() => setIsOpenCompletionWindow(false)}
            onClose={() => setIsOpenCompletionWindow(false)}
        >
          <CompletionModalWindow
              btnText={t('profileData.phoneDetails.completion.btnText')}
              handlerSetIsOpenModalWindow={() => setIsOpenCompletionWindow(false)}
              title={t('profileData.phoneDetails.completion.title')}
          />
        </Modal>
      </>
  );
};

export default PhoneDetails;